// @flow
import angular from 'angular';
import React, { Component } from 'react';
import { withRouter, type RouterHistory } from 'react-router-dom';
import 'app/mainapp/mainapp';
import type { AngularInjector, JqliteElement } from 'angular';

type AngularViewProps = {
  clientId: string,
  /**
   * This is only used to determine that the current view being rendered has changed, and we should
   * force a digest cycle. The content is ignored, it only matters that it's different between
   * views/
   */
  viewName: string,
  history: RouterHistory,
};

class AngularView extends Component<AngularViewProps> {
  static ngInjector: AngularInjector;

  componentDidMount() {
    /**
     * The base angular module is defined here to set up all the needed dependencies for
     * the angular pages, most importantly the ui-router setup. Some angular pages do default
     * re-directs between states handled by the ui-router. If the ui-router base state is not
     * set up, angular would get into an infinite digest loop when trying to route between states.
     */
    angular
      .module('bench.main-app', [])
      .constant('MainAppConfig', {
        routes: {
          'bench.dms': '/new-folders',
          'bench.details': '/details',
          'bench.categorizer': '/categorizer-rules',
          'bench.messaging': '/new-dashboard',
          'bench.accounts': '/accounts',
          'bench.ledgers': '/new-ledgers',
          'bench.reviewtool': '/review-tool',
        },
        clientId: this.props.clientId,
      })
      // $FlowFixMe - Flow doesn't have the type for the angular.config() we are using to set state
      .config($stateProvider => {
        $stateProvider.state('reconciliation', {
          url: `/app/${this.props.clientId}/reconciliation/:accStatementId`,
          onEnter: $stateParams => {
            this.props.history.push(
              `/app/${this.props.clientId}/reconciliation/${$stateParams.accStatementId}`,
            );
          },
        });
        $stateProvider.state('document-entry', {
          url: `/app/${this.props.clientId}/document-entry/:documentId/:navigationMode?:type`,
          onEnter: $stateParams => {
            this.props.history.push(
              `/app/${this.props.clientId}/document-entry/${$stateParams.documentId}/${$stateParams.navigationMode}`,
            );
          },
        });
      });
    // This is the initial mount so we need to bootstrap Angular and hang on to a reference
    // to the ngMountTarget for re-using later
    const ngMountTarget = this.createNgMountTarget();
    this.domHost.append(ngMountTarget);
    this.constructor.ngInjector = angular.bootstrap(ngMountTarget, ['bench.app.mainapp']);
  }

  componentDidUpdate(prevProps: AngularViewProps) {
    if (prevProps.viewName !== this.props.viewName) {
      // If we navigate from one view to another, we need to "kick" angular so the ui-router picks
      // up on the fact that the current location has changed
      this.forceDigest();
    }
  }
  get domHost(): JqliteElement {
    if (!this.domHostRef.current) {
      throw new Error('ng-app element is not yet mounted');
    }
    return angular.element(this.domHostRef.current);
  }
  domHostRef = React.createRef();

  createNgMountTarget(): JqliteElement {
    // we create this mount target element directly via DOM as we need to hang on to it and
    // re-attach it in ways that React really really doesn't want you to
    return angular.element(`
      <div ng-app="ng-app">
        <div class="rind-container">
          <ui-view></ui-view>
        </div>
      </div>
    `);
  }
  forceDigest() {
    const rootScope = this.constructor.ngInjector.get('$rootScope');
    // An ng digest cycle can actually be the trigger for a React update, so we will queue the
    // re-digest for the next "tick":
    setTimeout(() => rootScope.$digest(), 0);
  }
  render() {
    return <div ref={this.domHostRef} />;
  }
}

export default withRouter(AngularView);
